import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import portalApi from '../../../../lib/portalApi';
import {
  User,
  Palette,
  Bell,
  Lock,
  LogOut,
  Upload,
  Check,
  AlertCircle,
  Moon,
  Sun,
} from 'lucide-react';

export default function MentorSettingsPage() {
  const { user, logout, reload } = useAuth();
  const { theme, setTheme } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [avatar, setAvatar] = useState('');
  const [activeTheme, setActiveTheme] = useState('dark');
  const [notifications, setNotifications] = useState({
    tasks: { inApp: true, email: false },
    meetings: { inApp: true, email: false },
    deadlines: { inApp: true, email: false },
  });

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    portalApi
      .getMentorSettings()
      .then((res) => {
        if (res?.profile) {
          setFullName(res.profile.fullName || '');
          setEmail(res.profile.email || '');
          setPhone(res.profile.phone || '');
          setAvatar(res.profile.avatar || '');
        }
        if (res?.appearance?.theme) {
          setActiveTheme(res.appearance.theme);
        }
        if (res?.notifications) {
          setNotifications(res.notifications);
        }
      })
      .catch((err) => {
        console.error('Failed to load settings:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMsg('');
      setSuccessMsg('');
      await portalApi.updateMentorSettings({
        fullName,
        phone,
        theme: activeTheme,
        notifications,
      });
      if (setTheme) setTheme(activeTheme);
      await reload?.();
      setSuccessMsg('Profile and preferences updated successfully.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      setSaving(true);
      const res = await portalApi.uploadMentorAvatar(formData);
      if (res?.avatar) {
        setAvatar(res.avatar);
        await reload?.();
        setSuccessMsg('Avatar uploaded successfully.');
        setTimeout(() => setSuccessMsg(''), 4000);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Failed to upload avatar');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setErrorMsg('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match');
      return;
    }

    try {
      setPasswordSaving(true);
      setErrorMsg('');
      await portalApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMsg('Password changed successfully.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-xs text-[#8E8E99]">Loading mentor settings…</div>;
  }

  return (
    <div className="max-w-4xl space-y-8 animate-in fade-in duration-300">
      <div>
        <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">Settings</h1>
        <p className="text-xs text-[#8E8E99] mt-0.5">
          Manage your mentor profile, visual appearance, notification preferences and security.
        </p>
      </div>

      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
          <Check className="w-4 h-4" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* 1. Profile Section matching PDF Page 7 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#2C2C34]">
          <User className="w-4 h-4 text-[#D42B92]" />
          <h2 className="text-sm font-bold text-[#F2F2F4]">Profile Details</h2>
        </div>

        <form onSubmit={handleProfileSave} className="space-y-4 text-xs">
          {/* Avatar Upload */}
          <div className="flex items-center gap-4">
            {avatar ? (
              <img src={avatar} alt="" className="w-16 h-16 rounded-full object-cover border border-[#2C2C34]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#321129] border border-[#D42B92]/30 flex items-center justify-center text-xl font-bold text-[#D42B92]">
                {(fullName || 'M').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <label className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/40 text-[#F2F2F4] cursor-pointer transition-colors font-medium">
                <Upload className="w-3.5 h-3.5 text-[#D42B92]" />
                <span>Upload Photo</span>
                <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
              </label>
              <div className="text-[11px] text-[#8E8E99] mt-1">JPG, PNG or WEBP up to 5MB</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#8E8E99] font-medium mb-1">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
              />
            </div>

            <div>
              <label className="block text-[#8E8E99] font-medium mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                disabled
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#8E8E99] opacity-75 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-[#8E8E99] font-medium mb-1">Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* 2. Appearance Section (Dark / Light Theme) matching PDF Page 7 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#2C2C34]">
          <Palette className="w-4 h-4 text-[#D42B92]" />
          <h2 className="text-sm font-bold text-[#F2F2F4]">Appearance</h2>
        </div>

        <div className="grid grid-cols-2 gap-4 max-w-md">
          <button
            type="button"
            onClick={() => setActiveTheme('dark')}
            className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${
              activeTheme === 'dark'
                ? 'bg-[#321129] border-[#D42B92] text-[#F2F2F4]'
                : 'bg-[#08080A] border-[#2C2C34] text-[#8E8E99] hover:border-[#2C2C34]/80'
            }`}
          >
            <Moon className="w-5 h-5 text-[#D42B92]" />
            <span className="text-xs font-bold">Innovace Dark</span>
            <span className="text-[10px] text-[#8E8E99] text-center">Default dark visual style</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTheme('light')}
            className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${
              activeTheme === 'light'
                ? 'bg-[#321129] border-[#D42B92] text-[#F2F2F4]'
                : 'bg-[#08080A] border-[#2C2C34] text-[#8E8E99] hover:border-[#2C2C34]/80'
            }`}
          >
            <Sun className="w-5 h-5 text-amber-400" />
            <span className="text-xs font-bold">Clean Light</span>
            <span className="text-[10px] text-[#8E8E99] text-center">Alternative light palette</span>
          </button>
        </div>
      </div>

      {/* 3. Notifications Section matching PDF Page 7 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#2C2C34]">
          <Bell className="w-4 h-4 text-[#D42B92]" />
          <h2 className="text-sm font-bold text-[#F2F2F4]">Notification Preferences</h2>
        </div>

        <div className="divide-y divide-[#2C2C34]/40 text-xs">
          {[
            { key: 'tasks', label: 'New Tasks & Status Updates', desc: 'Alerts when superadmin assigns tasks or founder submits for review' },
            { key: 'meetings', label: 'Scheduled Meetings', desc: 'Reminders for upcoming startup mentoring sessions' },
            { key: 'deadlines', label: 'Milestone Deadlines', desc: 'Upcoming startup deliverable deadlines' },
          ].map((item) => (
            <div key={item.key} className="py-3 flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-[#F2F2F4]">{item.label}</div>
                <div className="text-[11px] text-[#8E8E99]">{item.desc}</div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications[item.key]?.inApp ?? true}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        [item.key]: {
                          ...notifications[item.key],
                          inApp: e.target.checked,
                        },
                      })
                    }
                    className="accent-[#D42B92] rounded"
                  />
                  <span className="text-[#8E8E99]">In-App</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer opacity-75">
                  <input
                    type="checkbox"
                    checked={notifications[item.key]?.email ?? false}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        [item.key]: {
                          ...notifications[item.key],
                          email: e.target.checked,
                        },
                      })
                    }
                    className="accent-[#D42B92] rounded"
                  />
                  <span className="text-[#8E8E99]">Email (Phase 2)</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4. Security: Change Password & Sign Out matching PDF Page 7 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#2C2C34]">
          <Lock className="w-4 h-4 text-[#D42B92]" />
          <h2 className="text-sm font-bold text-[#F2F2F4]">Security & Credentials</h2>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4 text-xs max-w-md">
          <div>
            <label className="block text-[#8E8E99] font-medium mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
          </div>

          <div>
            <label className="block text-[#8E8E99] font-medium mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
          </div>

          <div>
            <label className="block text-[#8E8E99] font-medium mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
          </div>

          <button
            type="submit"
            disabled={passwordSaving}
            className="px-5 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-medium transition-colors disabled:opacity-50"
          >
            {passwordSaving ? 'Updating…' : 'Change Password'}
          </button>
        </form>

        <div className="pt-4 border-t border-[#2C2C34] flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-[#F2F2F4]">Log Out of All Sessions</div>
            <div className="text-[11px] text-[#8E8E99]">Terminate your current session securely.</div>
          </div>
          <button
            onClick={() => logout()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
